// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { apiClient, API_ENDPOINTS } from '../config/api';

// Auth Context
const AuthContext = createContext();

// Auth Actions
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  CHECK_TOKEN: 'CHECK_TOKEN',
  SET_LOADING: 'SET_LOADING'
};

// Initial State
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Auth Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.CHECK_TOKEN:
      return {
        ...state,
        token: action.payload.token,
        isAuthenticated: action.payload.isAuthenticated,
        isLoading: false
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    default:
      return state;
  }
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Refresh user permissions from backend
  const refreshPermissions = async () => {
    if (!state.token) return;
    
    try {
      const response = await apiClient.get(API_ENDPOINTS.AUTH_ME, {
        headers: { Authorization: `Bearer ${state.token}` }
      });
      
      if (response.data) {
        const userData = {
          ...state.user,
          ...response.data
        };
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: userData, token: state.token }
        });
        
        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (error) {
      console.error('Failed to refresh permissions:', error);
    }
  };

  // Check for existing token on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          // Parse stored user data
          const user = JSON.parse(storedUser);
          
          // Verify token with backend
          const response = await apiClient.get(API_ENDPOINTS.AUTH_ME, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data && response.data.success !== false) {
            // Always use fresh data from backend, especially for permissions
            const userData = {
              ...response.data,
              // Only fallback to stored data for non-critical fields
              nickname: response.data.nickname || user.nickname
            };
            
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: { user: userData, token }
            });
          } else {
            // Backend says token is invalid, remove it
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
          }
        } catch (error) {
          console.warn('Auth check failed, using stored data:', error.message);
          
          // If backend is unreachable, use stored data temporarily
          // This prevents tabs from disappearing when backend is down
          const user = JSON.parse(storedUser);
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: { user, token }
          });
          
          // Try to refresh in background
          setTimeout(() => {
            refreshPermissions();
          }, 2000);
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    checkAuth();
  }, []);

  // Auto-refresh permissions when page gains focus (user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (state.isAuthenticated && state.token) {
        refreshPermissions();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [state.isAuthenticated, state.token]);

  // Login function
  const login = async (username, password) => {
    console.log('AuthContext: Starting login...', { username });
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      console.log('AuthContext: Making API call to:', API_ENDPOINTS.AUTH_LOGIN);
      const response = await apiClient.post(API_ENDPOINTS.AUTH_LOGIN, {
        username,
        password
      });

      console.log('AuthContext: API response:', response.status, response.data);
      
      if (response.data && response.data.access_token) {
        const { access_token, refresh_token, user } = response.data;
        
        console.log('AuthContext: Login successful, storing tokens and user data');
        // Store tokens and user data in localStorage
        localStorage.setItem('auth_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('user', JSON.stringify(user));
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token: access_token }
        });
        
        console.log('AuthContext: Returning success');
        return { success: true };
      } else {
        console.error('AuthContext: Invalid response from server:', response.data);
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  // Get current token
  const getToken = () => {
    return state.token || localStorage.getItem('auth_token');
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return state.isAuthenticated && state.token;
  };

  // Check if user has specific permission
  const hasPermission = (permissionCode) => {
    if (!state.user || !state.user.permissions) return false;
    return state.user.permissions.some((p) => {
      if (typeof p === 'string') {
        return p === permissionCode;
      }
      return p?.code === permissionCode || p?.permission_code === permissionCode;
    });
  };

  // Check if user has any permission from a list
  const hasAnyPermission = (permissionCodes) => {
    if (!state.user || !state.user.permissions) return false;
    return permissionCodes.some(code => hasPermission(code));
  };

  // Check if user has all permissions from a list
  const hasAllPermissions = (permissionCodes) => {
    if (!state.user || !state.user.permissions) return false;
    return permissionCodes.every(code => hasPermission(code));
  };

  // Get user permissions
  const getUserPermissions = () => {
    return state.user?.permissions || [];
  };

  // Check if user has admin role
  const isAdmin = () => {
    // user.role is a string (e.g., "Administrator", "IT", "User", etc.)
    return state.user?.role === 'Administrator' || state.user?.role === 'ADMIN';
  };

  // Check if user has QA Dashboard access
  const hasQADashboardAccess = () => {
    return hasPermission('QA_DASHBOARD_READ');
  };

  // Check if user can filter QA Dashboard
  // API only requires QA_DASHBOARD_READ for filter-options/summary/records; filters are part of viewing.
  const canFilterQADashboard = () => {
    return (
      hasPermission('QA_DASHBOARD_READ') ||
      hasPermission('QA_DASHBOARD_FILTER')
    );
  };

  // Check if user can export QA Dashboard
  const canExportQADashboard = () => {
    return hasPermission('QA_DASHBOARD_EXPORT');
  };

  // Auth Context Value
  const contextValue = {
    ...state,
    login,
    logout,
    getToken,
    refreshPermissions,
    isAuthenticated: isAuthenticated(),
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getUserPermissions,
    isAdmin,
    hasQADashboardAccess,
    canFilterQADashboard,
    canExportQADashboard
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;
